import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Box } from '../Box';
import { ImageRenderer } from '../ImageRenderer';
import { Skeleton } from '../Skeleton/Skeleton';
import { getAdaptiveAvatarSize, getAlt, getUrl } from './utils';
import { IAvatarProps } from './models';
import { classnames } from '@src/utils/classnames';
import { getClasses } from '@src/utils/getClass';
import styled from './Avatar.scss';
import { BreakpointsContext } from '@src/providers/Breakpoints';

/** Компонент для отображения автара */
export const Avatar: FC<IAvatarProps> = ({
  firstName = '',
  lastName = '',
  imageUrl,
  grayscale,
  online,
  loading,
  size = 'md',
  withBg = true,
  className,
  storage,
  id,
  onClick,
  ...props
}) => {
  const { breakpoint } = useContext(BreakpointsContext);

  const [url, setUrl] = useState<string | undefined>(getUrl({ storage, imageUrl }));
  const [loaded, setLoaded] = useState(false);

  const classes = classnames(
    className,
    styled.root,
    ...getClasses(
      styled,
      {
        size,
      },
      breakpoint,
    ),
    {
      [styled.withBackground]: !imageUrl && withBg,
      [styled.online]: online,
    },
  );

  const style =
    typeof size === 'number' || typeof size === 'object'
      ? { ...props.style, ...getAdaptiveAvatarSize(size, breakpoint) }
      : props.style;

  const handleLoaded = useCallback(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    setUrl(getUrl({ storage, imageUrl }));
  }, [imageUrl, storage]);

  if (loading) {
    return (
      <Skeleton className={classes} {...props} style={style}>
        <rect />
      </Skeleton>
    );
  }

  if (url) {
    return (
      <Box className={classes} {...props} data-id={id} onClick={onClick} style={style}>
        <ImageRenderer
          url={url}
          alt={loaded ? getAlt(firstName, lastName) : undefined}
          onLoaded={handleLoaded}
          greyscale={grayscale}
        />
      </Box>
    );
  }

  return (
    <Box className={classes} {...props} data-id={id} style={style} onClick={onClick}>
      {firstName?.slice(0, 1)}
      {lastName?.slice(0, 1)}
    </Box>
  );
};
